import React, { Component } from 'react';
import _ from 'lodash';
import Dropzone, { FileRejection } from 'react-dropzone';
import axios from 'axios/axios-base';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import {
  appendDSL,
  resetStoreState,
  startUploadTrackingThunk,
  toggleModal,
} from 'store/transactionDropzone/transactionDropzoneActions';
import DropzoneSpinner from 'components/loaders/DropzoneSpinner';
import { IToast, ToastType } from 'store/ui/toasts/toastsTypes';
import { blazeExtension, getTransactions } from 'store/transactions/transactionsActions';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import TagSelector from 'components/selectors/TagSelector';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import MixpanelService from 'utils/mixpanelService';
import { DROPZONE_CREATE_TRANSACTIONS, DROPZONE_FILE_DROPPED, OperationStatus } from 'utils/mixpanelEvents';
import { Form } from 'react-bootstrap';
import i18n from 'i18n';
import { getMarFileListThunk, toggleExpandedMarFilesRow } from 'store/marFiles/marFilesActions';
import { containsBothMarAndNonMarFiles, formatBytes, getFileExtension, isJsonFile } from 'utils/fileUtils';
import clsx from 'clsx';
import SubTitle from './subtitle/SubTitle';
import { BlazeMeterApiError, BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { isPrivateDataEnabled } from 'utils/bzmUserConfigUtil';
import { GenericDsl } from 'types/rest/dslTypes';
import { Service, Tag, Transaction } from 'types/rest/assetCatalogTypes';
import BzmIcon from '../icons/BzmIcon';
import { DEFAULT_POPPER_CONFIG, DROPZONE_ACCEPTABLE_FILES } from '../../constants/ui-constants';
import ElevioTooltip from '../overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import DropzoneVisual from './core/DropzoneVisual';

interface LocalState {
  droppedFiles: number;
  rejectedFiles: File[];
  acceptedFiles: File[];
  validFiles: File[];
  invalidFiles: File[];
  serviceId: any;
  isUploading: boolean;
  tags: Tag[];
  dropzoneClass: string;
  backendError: string;
  isTrackingInProgress: boolean;
  infoToast: IToast;
  blazeFormat: boolean;
}

interface TransactionsDropzoneProps {
  /** Context from where Dropzone is used */
  context: string;

  /** Id of the preselected service */
  preselectedService?: number;

  /** Processing of transaction finished, returns list of transactions created by dropZone */
  processingFinishedHandler?: (transactions?: Transaction[]) => void;

  /** Optional, set the title of the dropzone if you wish to override the default */
  title?: string;

  /** Optional, set the subtitle of the dropzone if you wish to override the default */
  subtitle?: any;

  /** Optional, set to true to disable the dropzone interactions */
  disabled?: boolean;

  /** List of accepted extensions, if not specified it defaults to ['.har', '.json', '.yaml', '.yml', '.zip', '.mar', '.wsdl']*/
  acceptFiles?: string[];
}

type MapToStateProps = ReturnType<typeof mapStateToProps>;

type DispatchPropsWithPromise = {
  startTracking: (trackingId: string, fileName: string) => Promise<void>;
};

type AllProps = MapToStateProps & DispatchProps & DispatchPropsWithPromise & TransactionsDropzoneProps;

const mapStateToProps = ({ transactionsDropzone, blazeUserConfig, ui }: ApplicationState) => ({
  dslResponses: transactionsDropzone.dslResponses,
  modalIsOpen: transactionsDropzone.modalIsOpen,
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
  activeServiceId: ui.pageFilter.serviceId,
  isReadOnlyInPrivateCloud: blazeUserConfig.isPrivateCloudReadOnly,
});

type DispatchProps = {
  toggleModal: () => void;
  appendDSL: (dsl: GenericDsl) => void;
  resetStoreState: () => void;
  getTransactions: () => void;
  showToastNotification: (type: ToastType, heading: string, content?: any, messageTimeout?: number) => IToast;
  getMarFileList: () => void;
  hideToast: (toast: IToast) => void;
  startTracking: (trackingId: string, fileName: string) => Promise<void>;
  toggleExpandedMarFilesRow: (assetId: number | null) => void;
};

const mapDispatchToProps = (dispatch: any) => ({
  toggleModal: () => dispatch(toggleModal()),
  appendDSL: (dsl: GenericDsl) => dispatch(appendDSL(dsl)),
  resetStoreState: () => dispatch(resetStoreState()),
  getTransactions: () => dispatch(getTransactions()),
  showToastNotification: (type: ToastType, heading: string, content?: any, messageTimeout?: number) =>
    dispatch(showToastNotification(type, heading, content, messageTimeout)),
  getMarFileList: () => dispatch(getMarFileListThunk()),
  hideToast: (toast: IToast) => dispatch(hideToast(toast)),
  toggleExpandedMarFilesRow: (assetId: number | null) => dispatch(toggleExpandedMarFilesRow(assetId)),
  startTracking: (trackingId: string, fileName: string) => dispatch(startUploadTrackingThunk(trackingId, fileName)),
});

type UploadType = 'SV' | 'TRANSACTIONAL';

class TransactionsDropzone extends Component<AllProps> {
  state: LocalState = {
    acceptedFiles: [],
    rejectedFiles: [],
    droppedFiles: 0,
    validFiles: [],
    invalidFiles: [],
    serviceId: this.props.preselectedService ? this.props.preselectedService : 0,
    isUploading: false,
    tags: [],
    dropzoneClass: 'dropzone mb-3',
    backendError: '',
    isTrackingInProgress: false,
    infoToast: {
      heading: '',
      content: '',
      timestamp: 0,
      type: ToastType.Info,
    },
    blazeFormat: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    // Preselection is enabled and preselected service has changed
    if (this.props.preselectedService !== nextProps.preselectedService) {
      this.setState({ serviceId: nextProps.preselectedService });
    }
  }

  /**
   * handle accepted files - valid format and size
   */
  onDropAccepted = <T extends File>(files: T[]): void => {
    this.setState({
      acceptedFiles: files,
      isUploading: true,
    });

    const containsMixedFiles = containsBothMarAndNonMarFiles(files);

    if (containsMixedFiles) {
      this.props.showToastNotification(ToastType.Danger, '', i18n.t('transactionsDropzone:errorMsgs.mixedFilesError'));
      this.resetState();
    } else {
      files.forEach(file => {
        if (isJsonFile(file)) {
          this.uploadFile(file, true);
        } else {
          this.uploadFile(file, false);
        }
      });
    }
  };

  /**
   * handle rejected files - invalid format or size
   * This method will be called if a file with invalid extension is uploaded.
   * The acceptable extensions are mentioned in Dropzone's accept property.
   */
  onDropRejected = <T extends FileRejection>(rejections: T[]) => {
    rejections.forEach(rejection => {
      this.props.showToastNotification(ToastType.Danger, '', i18n.t('transactionsDropzone:errorMsgs.invalidFile'));
    });
    this.resetState();
  };

  uploadFile = (file: File, svImport: boolean) => {
    const maxFileSize = this.props.blazeUserConfig ? this.props.blazeUserConfig.config.transactionsMaxFileSize : 0;
    this.setState({ isSvImport: svImport });
    if (file.size <= maxFileSize) {
      const formData = new FormData();
      formData.append('file', file);

      // Call convert endpoint if not in private cloud
      if (!this.isPrivateCloudInWriteMode() && !file.name.endsWith(blazeExtension)) {
        axios
          .post('/transactions/convert', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Accept: 'application/json',
            },
          })
          .then(response => {
            const result = response.data.result.map((item: any) => {
              return {
                dsl: item,
              };
            });
            this.props.appendDSL(result);
            this.setState({ validFiles: [...this.state.validFiles, file] });
            const fileExtension: string = getFileExtension(file);
            MixpanelService.track(
              DROPZONE_FILE_DROPPED(fileExtension, OperationStatus.SUCCESS, result.length, this.props.context),
              true,
            );
          })
          .catch(error => {
            this.setState({ invalidFiles: [...this.state.invalidFiles, file] });
            this.setState({ backendError: error.response.data.error });
            const fileExtension: string = getFileExtension(file);
            MixpanelService.track(
              DROPZONE_FILE_DROPPED(fileExtension, OperationStatus.ERROR, 0, this.props.context),
              true,
            );
          })
          .finally(() => {
            this.isUploadComplete();
          });
      } else {
        this.setState({
          validFiles: [...this.state.validFiles, file],
          blazeFormat: file.name.endsWith(blazeExtension),
        });
        const fileExtension: string = getFileExtension(file);
        MixpanelService.track(
          DROPZONE_FILE_DROPPED(fileExtension, OperationStatus.SUCCESS, 0, this.props.context),
          true,
        );
        this.isUploadComplete();
      }
    } else {
      const fileExtension: string = getFileExtension(file);
      this.setState({ invalidFiles: [...this.state.invalidFiles, file] });
      MixpanelService.track(DROPZONE_FILE_DROPPED(fileExtension, OperationStatus.ERROR, 0, this.props.context), true);
      this.resetState();
      this.props.showToastNotification(
        ToastType.Danger,
        '',
        i18n.t('transactionsDropzone:errorMsgs.fileBiggerThanRequired', { filesize: formatBytes(maxFileSize) }),
      );
    }
  };

  isUploadComplete = () => {
    // all files were converted => proceed with transaction creation
    if (this.state.validFiles.length === this.state.acceptedFiles.length) {
      this.props.toggleModal();
    } // files are corrupted - transactions/convert API call failed for all files
    else if (this.state.invalidFiles.length === this.state.acceptedFiles.length) {
      this.props.showToastNotification(
        ToastType.Danger,
        '',
        this.state.backendError ? this.state.backendError : i18n.t('transactionsDropzone:errorMsgs.corruptedFile'),
      );
      this.resetState();
    } else if (this.state.invalidFiles.length + this.state.validFiles.length === this.state.acceptedFiles.length) {
      const invalidFiles = this.state.invalidFiles;
      let invalidFilesMessage = `Files: `;
      invalidFiles.forEach(file => {
        invalidFilesMessage = invalidFilesMessage.concat(file.name + `, `);
      });
      this.props.showToastNotification(ToastType.Danger, '', i18n.t('transactionsDropzone:errorMsgs.invalidFiles'));
      // back to square one -- reset everything
      this.resetState();
    }
  };

  onModalImportButtonClick = () => {
    this.props.toggleModal();

    // Private data is enabled, and we are not in read only mode,
    // send files to different endpoint
    if (this.isPrivateCloudInWriteMode()) {
      this.uploadTransactionFilesInPrivateCloud();
    } else {
      if (this.state.blazeFormat === true) {
        // upload file directly instead of calling /convert endpoint
        this.uploadSvTransactionFilesInPublicCloud();
      } // In other cases
      else {
        this.setState({
          infoToast: this.props.showToastNotification(
            ToastType.Info,
            '',
            i18n.t('transactionsDropzone:infoMsgs.importingTxns'),
            0,
          ),
        });
        this.createTransactions();
      }
    }
  };

  /**
   * Checks whether private data is enabled and we are not in read only mode.
   */
  isPrivateCloudInWriteMode = () => {
    return isPrivateDataEnabled(this.props.blazeUserConfig) && !this.props.isReadOnlyInPrivateCloud;
  };

  onModalCancelButtonClick = () => {
    this.props.toggleModal();
    this.props.hideToast(this.state.infoToast);
    this.resetState();
    this.setState({
      isMar: false,
      isUploading: false,
    });
  };

  createTransactions = () => {
    return axios
      .post(
        this.getTransactionUrl(this.state.tags),
        { transactions: this.props.dslResponses },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      )
      .then(response => {
        const processedTransactions: Transaction[] = response.data.result;
        const numberOfTransactions = processedTransactions.length;
        this.props.hideToast(this.state.infoToast);
        this.props.showToastNotification(
          ToastType.Success,
          '',
          i18n.t('transactionsDropzone:successMsgs.transactionsImported', {
            numberOfTransactions: numberOfTransactions,
          }),
        );

        //Show warnings
        this.processAndShowWarnings(response.data);

        if (this.props.activeServiceId === 0 || this.state.serviceId === this.props.activeServiceId) {
          this.props.getTransactions();
        }

        MixpanelService.track(
          DROPZONE_CREATE_TRANSACTIONS(response.data.result.length, this.state.tags.length, OperationStatus.SUCCESS),
        );

        //If processing finished handler is available, call it
        if (this.props.processingFinishedHandler) {
          this.props.processingFinishedHandler(processedTransactions);
        }
      })
      .catch((error: any) => {
        this.props.hideToast(this.state.infoToast);
        this.props.showToastNotification(
          ToastType.Danger,
          '',
          i18n.t('transactionsDropzone:errorMsgs.transactionCreationFailed', {
            error: error.response ? error.response.data.error : 'Something went wrong.',
          }),
        );
        MixpanelService.track(DROPZONE_CREATE_TRANSACTIONS(0, this.state.tags.length, OperationStatus.ERROR, error));
      })
      .finally(() => {
        this.resetState();
      });
  };

  resetLocalState = () => {
    this.setState(
      {
        acceptedFiles: [],
        rejectedFiles: [],
        droppedFiles: 0,
        validFiles: [],
        invalidFiles: [],
        tags: [],
        dropzoneClass: 'dropzone mb-3',
        isUploading: false,
        infoToast: {
          heading: '',
          text: '',
          timestamp: 0,
          type: ToastType.Info,
        },
        blazeFormat: false,
      },
      () => {
        if (!this.props.preselectedService) {
          this.setState({ serviceId: 0 });
        }
      },
    );
  };

  resetState = () => {
    this.resetLocalState();
    this.props.resetStoreState();
  };

  selectServiceHandler = (service: Service | null) => {
    service != null ? this.setState({ serviceId: service.id }) : this.setState({ serviceId: 0 });
  };

  onDragEnter = () => {
    this.setState({ dropzoneClass: 'dropzone dz-drag-hover mb-3' });
  };

  onDragLeave = () => {
    this.setState({ dropzoneClass: 'dropzone mb-3' });
  };

  getSelectedTags = (tags: Tag[]) => {
    this.setState({ tags: tags });
  };

  /**
   * Starts upload tracking for particular file.
   * @param apiResponse
   * @param fileName
   * @param uploadType
   * @param fileName
   * @param uploadType
   */
  initializeUploadTracking = (apiResponse: BlazeMeterApiResponse, fileName: string, uploadType: UploadType) => {
    //Make sure response contains trackingId, if yes, continue
    const trackingId = _.get(apiResponse, 'result.trackingId');
    if (_.isEmpty(trackingId)) {
      this.props.showToastNotification(
        ToastType.Danger,
        i18n.t('transactionsDropzone:errorMsgs.trackingFailedTitle'),
        i18n.t('transactionsDropzone:errorMsgs.trackingFailedText'),
        0,
      );
      throw Error('Cannot start upload tracking');
    }

    // Create only one "In progress" notification for all uploads
    let toast: IToast | null;
    if (!this.state.isTrackingInProgress) {
      toast = this.props.showToastNotification(
        ToastType.Info,
        i18n.t('transactionsDropzone:infoMsgs.asyncProcessInProgressTitle'),
        i18n.t('transactionsDropzone:infoMsgs.asyncProcessInProgressText'),
        0,
      );
      this.setState({ isTrackingInProgress: true });
    }

    //Start polling, hide info when there is result available
    this.props
      .startTracking(trackingId, fileName)
      .then(() => {
        this.onAsyncUploadSuccessfullyFinished(uploadType);
      })
      .finally(() => {
        if (toast !== null && this.state.isTrackingInProgress === true) {
          this.props.hideToast(toast);
          this.setState({ isTrackingInProgress: false });
        }
      });
  };

  /**
   * When async upload finishes, list of assets is refreshed and also parent
   * component consuming dropzone is informed about the action result.
   * @param uploadType
   */
  onAsyncUploadSuccessfullyFinished = (uploadType: UploadType) => {
    console.debug('Async upload finished, calling the handlers, uploadType:', uploadType);

    if (uploadType === 'TRANSACTIONAL') {
      // Refresh main list of transactions
      this.props.getTransactions();

      // Call handler function if available
      if (this.props.processingFinishedHandler) {
        this.props.processingFinishedHandler();
      }
    }
  };

  dropZoneVisualComponent = () => {
    const dropZoneDefaultSubtitle = <SubTitle />;
    return (
      <DropzoneVisual
        title={this.props.title ? this.props.title : i18n.t('transactionsDropzone:Dropzone.title')}
        subtitle={this.props.subtitle ? this.props.subtitle : dropZoneDefaultSubtitle}
      />
    );

    return (
      <div className="d-flex justify-content-center text-left">
        <BzmIcon variant={'icon-bzm-upload-cloud'} className={'icon-jb mr-4'} />
        <div>
          <h1 className="mb-2">
            {this.props.title ? this.props.title : i18n.t('transactionsDropzone:Dropzone.title')}
          </h1>
          {this.props.subtitle ? this.props.subtitle : dropZoneDefaultSubtitle}
        </div>
      </div>
    );
  };

  /**
   * Builds Upload URL with tags.
   * TODO - put tags to axios as params or via upload form not by URL
   * @param tags
   */
  getTransactionUrl = (tags: Tag[]) => {
    let url = `/workspaces/{workspaceId}/transactions?serviceId=${this.state.serviceId}`;
    if (tags.length > 0) {
      const tagz = tags.map((tag: Tag) => {
        return tag.name;
      });
      url += `&tags=${tagz.toString()}`;
    }
    return url;
  };

  /**
   * Upload files (used in private cloud). Uploads multiple selected files
   * one by one synchronously to avoid async problems.
   * */
  uploadTransactionFilesInPrivateCloud = async () => {
    this.setState({
      infoToast: this.props.showToastNotification(
        ToastType.Info,
        '',
        i18n.t('transactionsDropzone:infoMsgs.importingTxns'),
        0,
      ),
    });

    const uploadedFiles = this.state.acceptedFiles.length;
    if (uploadedFiles > 0) {
      for (let i = 0; i < uploadedFiles; i++) {
        const formData = new FormData();
        formData.append('file', this.state.acceptedFiles[i]);

        try {
          // Upload files one by one
          await axios
            .post(this.getTransactionUrl(this.state.tags), formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Accept: 'application/json',
              },
            })
            .then(result => {
              const response: BlazeMeterApiResponse = result.data;
              this.initializeUploadTracking(response, this.state.acceptedFiles[i].name, 'TRANSACTIONAL');
              console.debug('Initialisation of tracking started.');
            });
        } catch (error) {
          this.props.showToastNotification(
            ToastType.Danger,
            '',
            i18n.t('learnMoreDropzone:errorMsgs.transactionCreationFailed', {
              error: error.response ? error.response.data.error : 'Something went wrong.',
            }),
          );
        }
      }
    }

    // Hide "Your uploaded files are being processed by the server..."
    this.props.hideToast(this.state.infoToast);
    this.resetState();
  };

  /**
   * Processes API response and shows warnings.
   * @param response
   */
  processAndShowWarnings = (response: BlazeMeterApiResponse) => {
    const errors = _.get(response, 'errors');
    if (!_.isNil(errors)) {
      _.forEach(errors, (error: BlazeMeterApiError) => {
        const toastType = error.severity === 'WARN' ? ToastType.Warning : ToastType.Danger;
        this.props.showToastNotification(toastType, 'Warning', error.message, 15000);
      });
    }
  };

  /**
   * Upload files (used in public cloud flow if user uploads SV Transaction json file). Uploads multiple selected files
   * one by one synchronously to avoid async problems.
   * */
  uploadSvTransactionFilesInPublicCloud = async () => {
    this.setState({
      infoToast: this.props.showToastNotification(
        ToastType.Info,
        '',
        i18n.t('transactionsDropzone:infoMsgs.importingTxns'),
        0,
      ),
    });

    const uploadedFiles = this.state.acceptedFiles.length;
    if (uploadedFiles > 0) {
      for (let i = 0; i < uploadedFiles; i++) {
        const formData = new FormData();
        formData.append('file', this.state.acceptedFiles[i]);

        try {
          // Upload files one by one
          await axios
            .post(this.getTransactionUrl(this.state.tags), formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Accept: 'application/json',
              },
            })
            .then(result => {
              const response: BlazeMeterApiResponse = result.data;
              const processedTransactions: Transaction[] = response.result;
              const numberOfTransactions = processedTransactions.length;

              this.props.showToastNotification(
                ToastType.Success,
                '',
                i18n.t('transactionsDropzone:successMsgs.transactionsImported', {
                  numberOfTransactions: numberOfTransactions,
                }),
              );

              //Show warnings
              this.processAndShowWarnings(result.data);

              if (this.props.activeServiceId === 0 || this.state.serviceId === this.props.activeServiceId) {
                this.props.getTransactions();
              }
              MixpanelService.track(
                DROPZONE_CREATE_TRANSACTIONS(response.result.length, this.state.tags.length, OperationStatus.SUCCESS),
              );

              //If processing finished handler is available, call it
              if (this.props.processingFinishedHandler) {
                this.props.processingFinishedHandler(processedTransactions);
              }
            });
        } catch (error) {
          const errorDesc = i18n.t('transactionsDropzone:errorMsgs.transactionCreationFailedFromJson');
          let errorMsg = error.response ? error.response.data.error : 'Something went wrong.';
          if (_.includes(errorMsg, 'No Transactions can be created from provided sources')) {
            errorMsg = errorDesc;
          }
          this.props.showToastNotification(ToastType.Danger, '', errorMsg);
        } finally {
          this.props.getTransactions();
        }
      }
    }

    // Hide "Your uploaded files are being processed by the server..."
    this.props.hideToast(this.state.infoToast);
    this.resetState();
  };

  render() {
    const modalTitle = 'transactionsDropzone:modal.title';
    return (
      <React.Fragment>
        {this.props.modalIsOpen && (
          <Modal show={true} centered={true} backdrop={true} onHide={this.onModalCancelButtonClick} size="sm">
            <Modal.Header closeButton id={'qa-view-dropzone-modal-btn-close-icon'}>
              <h5 className="modal-title" id="exampleModalLabel">
                {i18n.t(modalTitle)}
              </h5>
            </Modal.Header>
            <Modal.Body>
              <div className={'form-group'}>
                <span>
                  <label htmlFor="dropZoneServicePicker">{i18n.t('transactionsDropzone:modal.serviceLabel')}</label>
                  <ElevioTooltip
                    elevioId={'Services_import_transactions'}
                    title={i18n.t('transactionsDropzone:modal.serviceElevioTooltip')}
                  />
                </span>
                <ServicePicker
                  selectHandler={(service: Service | null) => this.selectServiceHandler(service)}
                  isSelectAll={false}
                  isCreateEnabled={true}
                  manualPreLoad={true}
                  preSelected={this.state.serviceId}
                  maxHeight="230px"
                  popperConfig={DEFAULT_POPPER_CONFIG}
                  id="dropZoneServicePicker"
                  focus={true}
                />
              </div>
              <Form.Group controlId="qa-input-tags-dropzone">
                <label htmlFor={'react-select-multiple'}>{i18n.t('transactionsDropzone:modal.tagsLabel')}</label>
                <TagSelector selectHandler={this.getSelectedTags} />
              </Form.Group>
              {/*<Link to="/topics">Link Advanced Import Options</Link>*/}
            </Modal.Body>
            <Modal.Footer>
              <Button id={'qa-dropzone-modal-btn-cancel'} variant="link" onClick={this.onModalCancelButtonClick}>
                {i18n.t('transactionsDropzone:modal.buttons.cancel')}
              </Button>
              <Button
                disabled={this.state.serviceId === 0}
                id={'qa-dropzone-modal-btn-import'}
                variant="primary"
                onClick={this.onModalImportButtonClick}
              >
                {i18n.t('transactionsDropzone:modal.buttons.import')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.state.isUploading ? (
          <form action="/file-upload" className="dropzone dz-clickable" id="dropzone-upload-transactions">
            <div className="dz-message">
              <div className="dropzone-overlay">
                <div className="dropzone-overlay-blurred">{this.dropZoneVisualComponent()}</div>
                <DropzoneSpinner />
              </div>
            </div>
          </form>
        ) : (
          <div
            id={'qa-comp-dropzone'}
            className={clsx(this.state.dropzoneClass, this.props.disabled && 'dropzone-disabled')}
            style={this.props.disabled ? { cursor: 'default' } : { cursor: 'pointer' }}
          >
            {/* No need to set maxSize on the drop zone - file size validation is performed in uploadFile function. */}
            <Dropzone
              onDropRejected={this.onDropRejected}
              onDropAccepted={this.onDropAccepted}
              accept={this.props.acceptFiles ? this.props.acceptFiles : DROPZONE_ACCEPTABLE_FILES}
              onDragEnter={this.onDragEnter}
              onDragLeave={this.onDragLeave}
              disabled={this.props.disabled}
            >
              {({ getRootProps, getInputProps }) => (
                <div className={'dz-message'} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {this.dropZoneVisualComponent()}
                </div>
              )}
            </Dropzone>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TransactionsDropzone);
